/**
 * The work-order runner: which order is active, what it armed, and what the
 * journal has proven since.
 *
 * A module singleton, like the house bus, and for the same reason: an order
 * is a fact about the session, not about any one panel. Panels and HUD widgets
 * subscribe; the runner never reaches into them.
 *
 * THE PENDING-FAULT HANDSHAKE. House events arm directly on the bus. Tool
 * faults cannot: ShiftReports are created lazily inside panels the student
 * may not have opened yet. So accepting an order records the desired fault
 * per tool, and every ShiftReport asks `pendingFaultFor(toolId)` on
 * construction and on each broadcast - the same shape as `houseScenarios`,
 * pointed the other way. This is the mission layer's only touch inside the
 * shift machinery.
 */

import type { HouseBus } from '../sim/house/model'
import type { Journal } from './Journal'
import { shiftCost } from './shift'
import { evaluate, missionById, MISSIONS, type Mission } from './missions'

const STORE_KEY = 'simfab.workorders.v1'

interface Stored {
  completed: string[]
  active: string | null
  /** Journal index the active order started at. */
  startIndex: number
}

export interface MissionProgress {
  mission: Mission
  met: boolean[]
  done: boolean
}

export class MissionState {
  private journal!: Journal
  private house!: HouseBus
  private active: Mission | null = null
  private startIndex = 0
  private completed = new Set<string>()
  /** toolId -> fault id this order wants armed on that tool's next shift. */
  private pending = new Map<string, string>()
  private readonly listeners = new Set<() => void>()
  /** Fires once per completion, after `completed` is updated. */
  onComplete: ((progress: MissionProgress) => void) | null = null

  attach(journal: Journal, house: HouseBus): void {
    this.journal = journal
    this.house = house
    this.restore()
    journal.subscribe(() => this.tick())
    // The bus too: a repair journals its maintenance event BEFORE the house
    // clears, so an order whose last objective is houseCleared would evaluate
    // one beat too early and then never again. The building's own state
    // change is evidence, and evidence re-evaluates the order.
    house.subscribe(() => this.tick())
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private broadcast(): void {
    for (const l of [...this.listeners]) l()
  }

  get activeMission(): Mission | null {
    return this.active
  }

  isCompleted(id: string): boolean {
    return this.completed.has(id)
  }

  /** What the active order wants armed at this tool, if anything. */
  pendingFaultFor(toolId: string): string | null {
    return this.pending.get(toolId) ?? null
  }

  accept(mission: Mission): void {
    this.active = mission
    this.startIndex = this.journal.events.length
    this.pending.clear()
    if (mission.setup.toolFault) {
      this.pending.set(mission.setup.toolFault.tool, mission.setup.toolFault.fault)
    }
    // Arming the house is honest even when the order arms nothing: null is
    // "the building is healthy", which is also a world state.
    this.house.arm(mission.setup.house ?? null)
    this.persist()
    this.broadcast()
  }

  abandon(): void {
    this.active = null
    this.pending.clear()
    this.house.arm(null)
    this.persist()
    this.broadcast()
  }

  /** Live progress of the active order, or null. */
  progress(): MissionProgress | null {
    if (!this.active) return null
    const slice = this.journal.events.slice(this.startIndex)
    const { met, done } = evaluate(this.active, slice, this.house.armed === null)
    return { mission: this.active, met, done }
  }

  private tick(): void {
    if (!this.active) return
    const p = this.progress()
    if (!p) return
    if (p.done) {
      const slice = this.journal.events.slice(this.startIndex)
      this.completed.add(this.active.id)
      this.active = null
      this.pending.clear()
      this.persist()
      // Into the record, not just the UI store: the journal is the one
      // document a student can hand an instructor, and a completion that
      // lived only in localStorage would be invisible in it. Recorded after
      // `active` clears so this very event cannot re-trigger evaluation.
      this.journal.record({
        kind: 'orderComplete',
        order: p.mission.id,
        title: p.mission.title,
        hours: shiftCost(slice).totalHours,
      })
      this.onComplete?.(p)
    }
    this.broadcast()
  }

  /** Journal slice of the order just finished or in progress, for debriefs. */
  sliceSince(): number {
    return this.startIndex
  }

  private persist(): void {
    try {
      const stored: Stored = {
        completed: [...this.completed],
        active: this.active?.id ?? null,
        startIndex: this.startIndex,
      }
      localStorage.setItem(STORE_KEY, JSON.stringify(stored))
    } catch {
      /* private windows and locked-down browsers simply do not persist */
    }
  }

  private restore(): void {
    try {
      const raw = localStorage.getItem(STORE_KEY)
      if (!raw) return
      const stored = JSON.parse(raw) as Stored
      this.completed = new Set(
        stored.completed.filter((id) => MISSIONS.some((m) => m.id === id)),
      )
      // An active order does NOT survive a reload: its journal slice and its
      // armed world state may not have. Progress ticks are cheap to re-earn;
      // a half-restored order that can never complete is not.
      if (stored.active && missionById(stored.active)) {
        this.active = null
      }
    } catch {
      /* an unreadable store is an empty one */
    }
  }
}

/** The one runner for the session. */
export const missionState = new MissionState()
